import { Component, HostListener, Input, NgZone, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  ColDef, ColumnState, GridApi, GridOptions, RowClassParams, CellClickedEvent, NewColumnsLoadedEvent, SortChangedEvent,
} from 'ag-grid-community';
import { DayStat, HourlyDataPoint, PelletDayStat } from '../version.service';
import { formatKwh, fmtDate, fmtMonth, fmtMonthYear, fmtDayMonthYear, clampAutarkie } from '../shared/format.utils';
import { lockScroll, unlockScroll } from '../shared/scroll-lock';
import { DeepLinkService } from '../shared/deep-link.service';
import { uiLocale } from '../shared/chart.utils';
import { leaveSheetEntry, pushSheetEntry } from '../popup-card/sheet-history';

/** A day as the hourly overlay's view segment ('statistik/2026-10-18'). */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** View of the fault-days sheet (mobile ⓘ). */
const FAULTS_VIEW = 'statistik/faults';
/** Sort of the energy grids by default (as written in the link: "<colId>:<asc|desc>"). */
const DEFAULT_SORT = 'date:desc';
/** Below 768 px (the one-tab view): essential columns with symbol / unit headers (a legend line explains
 *  them) and numbers without units; the grids grow with the page (no inner scrolling) and show a few rows
 *  first (the last two weeks, a chosen month whole, the last year of months); "show more" adds a month of
 *  days / a year of months. */
const COMPACT_MAX_W = 768;
const COMPACT_DAYS  = 14;
const MONTH_DAYS    = 31;
const YEAR_MONTHS   = 12;

@Component({
  selector: 'app-statistik',
  templateUrl: './statistik.component.html',
  styles: [`
    .stat-ctrl { display: flex; align-items: center; gap: .5rem; margin-bottom: .5rem; }
    .stat-fault-row { padding: .5rem 0; border-top: 1px solid var(--bs-border-color-translucent); }
    /* compact grids: tighter cells, so the essential columns fit a phone without sideways scrolling */
    .stat-grid-compact { --ag-cell-horizontal-padding: 8px; --ag-header-column-resize-handle-display: none; }
    @media (max-width: 767.98px) {
      /* year buttons share the row (the broken-data buttons wrap below them when the row is too narrow) */
      .stat-years { flex: 1 0 auto; }
      .stat-years > .btn { flex: 1 0 auto; }
      /* … and the broken-data toggle fills the rest (or its own line, as the longer German label does) */
      .broken-data-wrap { flex: 1 0 auto; }
      .broken-data-wrap > .btn:first-child { flex: 1 0 auto; }
      .stat-filter .btn { min-height: var(--dash-touch-sm); }
      .stat-filter .btn-outline-secondary { --bs-btn-color: var(--bs-secondary-color); }   /* readable in both themes */
      .stat-info-btn { min-width: var(--dash-touch-sm); }
      /* month select takes the free width, the daily / monthly switch keeps its natural width */
      .stat-ctrl > .form-select { flex: 1 1 auto; width: auto; min-width: 0; min-height: var(--dash-touch-sm); }
      .stat-ctrl > .dash-seg { flex: 0 0 auto; width: auto; }
      .stat-ctrl > .dash-seg > .btn { flex: 0 0 auto; min-height: var(--dash-touch-sm); padding-left: .75rem; padding-right: .75rem; }
      .stat-more { min-height: var(--dash-touch-sm); --bs-btn-color: var(--bs-secondary-color); }
    }
    @media (max-width: 575.98px) {
      .stat-grid-compact { --ag-cell-horizontal-padding: 6px; --ag-font-size: 12px; }
      .stat-info-warn { display: block; }   /* the data-quality note on a line of its own */
    }
    @media (max-width: 359.98px) {
      .stat-grid-compact { --ag-cell-horizontal-padding: 4px; --ag-font-size: 11px; }   /* 320 px: 6 columns fit */
    }
  `],
})
export class StatistikComponent implements OnChanges, OnInit, OnDestroy {
  @Input() stats:   DayStat[] = [];
  @Input() pelletStats: PelletDayStat[] = [];
  @Input() loading  = false;
  @Input() darkMode = false;

  private _langSub?: Subscription;
  private _linkSub?: Subscription;

  readonly Math = Math;
  /** One-tab view (< 768 px): compact grids (see COMPACT_MAX_W). */
  compact = window.innerWidth < COMPACT_MAX_W;
  /** Rows shown by the compact energy / pellet grid (grows per "show more", resets with a filter). */
  statsRowLimit  = COMPACT_DAYS;
  pelletRowLimit = COMPACT_DAYS;
  statsMonthFilter     = 'all';
  statsView: 'daily' | 'monthly' = 'daily';
  globalYearFilter     = 'all';
  includeBrokenData    = false;
  faultInfoOpen        = false;

  statsColDefs:        ColDef[] = [];
  monthlyStatsColDefs: ColDef[] = [];

  readonly SYSTEM_COST_EUR   = 25_000;
  readonly ELECTRICITY_PRICE = 0.30;
  readonly FEEDIN_TARIFF     = 0.082;
  readonly CO2_FACTOR        = 0.380;

  pelletStatsView: 'daily' | 'monthly' = 'daily';
  pelletColDefs: ColDef[] = [];
  pelletMonthlyColDefs: ColDef[] = [];

  readonly statsGridOptions: GridOptions = {
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    tooltipShowDelay: 300,
    enableBrowserTooltips: true,
    rowStyle: { cursor: 'pointer' },
    // Stable row identity → AG Grid diffs instead of full-reset, preserving scroll position
    getRowId: (p) => p.data.date,
    rowClassRules: {
      'row-fault': (p: RowClassParams) => !!p.data?.faultReason,
    },
    // compact: flex columns fill the width (autosizing would undo that)
    onFirstDataRendered: (e) => { if (!this.compact) e.api.autoSizeAllColumns(); },
    onGridReady: (e) => { this._dailyApi = e.api; this.applySort(e.api); },
    onGridPreDestroyed: (e) => { if (this._dailyApi === e.api) this._dailyApi = null; },
    onSortChanged: (e) => this.onSortChanged(e),
    onNewColumnsLoaded: (e) => this.onNewColumnsLoaded(e),
    onCellClicked: (e: CellClickedEvent) => this.openHourly(e.data?.date, e.colDef.field ?? null),
    onCellDoubleClicked: (e) => {
      const val = e.value != null ? String(e.value) : '';
      navigator.clipboard.writeText(val).catch(() => {});
    },
  };

  readonly monthlyGridOptions: GridOptions = {
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    getRowId: (p) => p.data.date,
    onFirstDataRendered: (e) => { if (!this.compact) e.api.autoSizeAllColumns(); },
    onGridReady: (e) => { this._monthlyApi = e.api; this.applySort(e.api); },
    onGridPreDestroyed: (e) => { if (this._monthlyApi === e.api) this._monthlyApi = null; },
    onSortChanged: (e) => this.onSortChanged(e),
    onNewColumnsLoaded: (e) => this.onNewColumnsLoaded(e),
  };

  /** The energy grid on screen (daily or monthly — the other one does not exist). */
  private _dailyApi:   GridApi | null = null;
  private _monthlyApi: GridApi | null = null;
  /** The pellet grid on screen (daily or monthly share pelletGridOptions; only one exists at a time). */
  private _pelletApi:  GridApi | null = null;

  // ── Hourly chart overlay ────────────────────────────────────────────────
  /** Fields that map to a dedicated dataset in the hourly chart. */
  private static readonly HOURLY_DATASET_FIELDS = new Set([
    'generationKwh', 'consumptionKwh', 'gridImportKwh', 'gridExportKwh',
  ]);

  hourlyDate:    string | null      = null;
  /** null = all datasets; otherwise the field key of the single dataset to show. */
  hourlyField:   string | null      = null;
  hourlyData:    HourlyDataPoint[]  = [];
  hourlyLoading  = false;
  hourlyError    = false;
  private readonly _hourlyCache = new Map<string, HourlyDataPoint[]>();

  /** Deep-link view of the overlay ('statistik/<date>') — also the id of its chart. */
  get hourlyLinkId(): string | null { return this.hourlyDate ? 'statistik/' + this.hourlyDate : null; }

  /** Open the overlay for a day (`field`: the clicked energy column → that series as bars).
   *  `write`: false when following the link. A tap opens it in a history entry of its own (Back closes it). */
  openHourly(date: string | undefined, field: string | null, write = true): void {
    if (!date || !DATE_RE.test(date)) return;
    if (write) {
      const view = 'statistik/' + date;
      if (!this.link.isOpen(view)) pushSheetEntry(view, this.link, this.zone);
      this.link.openView(view);
      this.link.set('field', this.hourlyFieldOf(field));
    }
    if (!this.hourlyDate) lockScroll();
    this.hourlyDate  = date;
    this.hourlyField = this.hourlyFieldOf(field);
    this.hourlyError = false;

    if (this._hourlyCache.has(date)) {
      this.hourlyData    = this._hourlyCache.get(date)!;
      this.hourlyLoading = false;
      return;
    }

    this.hourlyLoading = true;
    this.hourlyData    = [];
    fetch(`/api/hourly?date=${date}`)
      .then(r => r.json())
      .then((data: HourlyDataPoint[]) => {
        this._hourlyCache.set(date, data);
        if (this.hourlyDate === date) {
          this.hourlyData    = data;
          this.hourlyLoading = false;
        }
      })
      .catch(() => {
        if (this.hourlyDate === date) {
          this.hourlyError   = true;
          this.hourlyLoading = false;
        }
      });
  }

  /** `write`: false when following the link; ✕ / a tap outside also goes back over the tap's history entry. */
  closeHourly(write = true): void {
    if (!this.hourlyDate) return;
    const view = 'statistik/' + this.hourlyDate;
    this.hourlyDate = null;
    unlockScroll();
    if (write) { this.link.closeView(view); leaveSheetEntry(view); }
  }

  private hourlyFieldOf(field: string | null): string | null {
    return field && StatistikComponent.HOURLY_DATASET_FIELDS.has(field) ? field : null;
  }

  hourlyFieldLabel(field: string | null): string {
    const keys: Record<string, string> = {
      generationKwh:  'COLS.ERTRAG',
      consumptionKwh: 'COLS.VERBRAUCH',
      gridImportKwh:  'COLS.BEZUG',
      gridExportKwh:  'COLS.EINSPEISUNG',
    };
    return field && keys[field] ? this.translate.instant(keys[field]) : '';
  }

  /** The overlay's day with its weekday ("Sa., 18.10.2026" / "Sat, 18/10/2026"). */
  get hourlyTitle(): string {
    if (!this.hourlyDate) return '';
    return new Date(this.hourlyDate + 'T00:00:00').toLocaleDateString(
      uiLocale(), { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  /** A "YYYY-MM-DD" day as "18.10.2026" / "18/10/2026" (anchored to local midnight). */
  fmtDay(date: string | null | undefined): string {
    return date ? new Date(date + 'T00:00:00').toLocaleDateString(uiLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  }

  /** The system's cost with the UI locale's grouping ("25.000" / "25,000"). */
  get systemCost(): string { return this.SYSTEM_COST_EUR.toLocaleString(uiLocale()); }

  /** The overlay day's row: its sunrise / sunset and fault reason are shown above the chart (the compact
   *  grid leaves those columns out). */
  get hourlyDay(): DayStat | null {
    return this.hourlyDate ? this.stats.find(s => s.date === this.hourlyDate) ?? null : null;
  }

  /** Short name of a fault reason (badges in the fault-days list). */
  faultLabel(reason: string | null | undefined): string {
    const keys: Record<string, string> = {
      WIFI_OUTAGE: 'FAULT_TYPE_WIFI', TIGO_OUTAGE: 'FAULT_TYPE_TIGO', DEYE_FREEZE: 'FAULT_TYPE_FREEZE',
      NO_YIELD: 'FAULT_TYPE_NO_YIELD', DATA_MISSING: 'FAULT_TYPE_DATA',
    };
    return reason && keys[reason] ? this.translate.instant(keys[reason]) : (reason ?? '');
  }

  /** Crossing 768 px: other columns (compact or not). */
  @HostListener('window:resize')
  onResize(): void {
    const compact = window.innerWidth < COMPACT_MAX_W;
    if (compact === this.compact) return;
    this.compact = compact;
    this.buildColDefs();
    this.buildPelletColDefs();
  }

  constructor(readonly translate: TranslateService, private readonly link: DeepLinkService, private readonly zone: NgZone) {}

  ngOnInit(): void {
    this.buildColDefs();
    this.buildPelletColDefs();
    this._langSub = this.translate.onLangChange.subscribe(() => { this.buildColDefs(); this.buildPelletColDefs(); });
    this.applyLink();
    this._linkSub = this.link.external$.subscribe(() => this.applyLink());
  }

  ngOnDestroy(): void {
    this._langSub?.unsubscribe();
    this._linkSub?.unsubscribe();
    if (this.hourlyDate) unlockScroll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stats']) this.validateFilters();
  }

  // ── Deep link ───────────────────────────────────────────────────────────
  // statistik.year / .month / .view / .broken / .pelletView / .sort / .pelletSort (filters of the tab), the
  // hourly overlay as view 'statistik/<YYYY-MM-DD>' with 'field' (and its chart's hide / show / from / to),
  // the fault-days sheet (mobile) as view 'statistik/faults'.

  /** Apply the link (on init and when the browser navigates). */
  private applyLink(): void {
    const l = this.link;
    const before = this.filterKey();
    const year  = l.get('statistik.year');
    const month = l.get('statistik.month');
    this.globalYearFilter = year && /^\d{4}$/.test(year) ? year : 'all';
    this.statsMonthFilter = month && /^\d{4}-\d{2}$/.test(month)
      && (this.globalYearFilter === 'all' || month.startsWith(this.globalYearFilter + '-')) ? month : 'all';
    this.statsView        = l.get('statistik.view') === 'monthly' ? 'monthly' : 'daily';
    this.includeBrokenData = l.get('statistik.broken') === '1';
    this.pelletStatsView  = l.get('statistik.pelletView') === 'monthly' ? 'monthly' : 'daily';
    this.validateFilters();
    // other filters from the link: the compact grids start short again (opening a day keeps "show more")
    if (this.filterKey() !== before) {
      this.statsRowLimit  = this.statsFirstRows();
      this.pelletRowLimit = this.pelletFirstRows();
    }
    // a grid that is (re)created applies the sort itself (onGridReady)
    this.applySort(this.statsView === 'daily' ? this._dailyApi : this._monthlyApi);
    this.applySort(this._pelletApi, 'statistik.pelletSort');

    const date = l.childOf('statistik');
    this.faultInfoOpen = date === 'faults';
    if (date && DATE_RE.test(date)) {
      const field = l.get('field');
      if (date !== this.hourlyDate || this.hourlyFieldOf(field) !== this.hourlyField) this.openHourly(date, field, false);
    } else {
      this.closeHourly(false);
    }
  }

  private filterKey(): string {
    return [this.globalYearFilter, this.statsMonthFilter, this.statsView, this.includeBrokenData, this.pelletStatsView].join('|');
  }

  /** Year / month filters that name no data (once the stats are there) fall back to 'all'. */
  private validateFilters(): void {
    if (!this.stats.length) return;
    if (this.globalYearFilter !== 'all' && !this.globalStatYears.includes(this.globalYearFilter)) this.setGlobalYear('all');
    if (this.statsMonthFilter !== 'all' && !this.statsMonths.includes(this.statsMonthFilter)) this.setStatsMonth('all');
  }

  setStatsMonth(month: string): void {
    this.statsMonthFilter = month;
    this.statsRowLimit = this.statsFirstRows();
    this.link.set('statistik.month', month === 'all' ? null : month);
  }

  /** Daily / monthly grid: the new grid starts with its default sort. */
  setStatsView(view: 'daily' | 'monthly'): void {
    if (view === this.statsView) return;
    this.statsView = view;
    this.statsRowLimit = this.statsFirstRows();
    this.link.set('statistik.view', view === 'daily' ? null : view);
    this.link.set('statistik.sort', null);
  }

  toggleBrokenData(): void {
    this.includeBrokenData = !this.includeBrokenData;
    this.statsRowLimit = this.statsFirstRows();
    this.link.set('statistik.broken', this.includeBrokenData ? 1 : null);
  }

  /** Daily / monthly pellet grid: the new grid starts with its default sort. */
  setPelletStatsView(view: 'daily' | 'monthly'): void {
    if (view === this.pelletStatsView) return;
    this.pelletStatsView = view;
    this.pelletRowLimit = this.pelletFirstRows();
    this.link.set('statistik.pelletView', view === 'daily' ? null : view);
    this.link.set('statistik.pelletSort', null);
  }

  /** Compact grids: rows shown first (see COMPACT_DAYS) and per "show more". */
  private statsFirstRows(): number {
    return this.statsView === 'monthly' ? YEAR_MONTHS : this.statsMonthFilter === 'all' ? COMPACT_DAYS : MONTH_DAYS;
  }
  private pelletFirstRows(): number { return this.pelletStatsView === 'monthly' ? YEAR_MONTHS : COMPACT_DAYS; }
  showMoreStats(): void  { this.statsRowLimit  += this.statsView === 'monthly' ? YEAR_MONTHS : MONTH_DAYS; }
  showMorePellets(): void { this.pelletRowLimit += this.pelletStatsView === 'monthly' ? YEAR_MONTHS : MONTH_DAYS; }

  /** Fault-days sheet (mobile ⓘ) as view 'statistik/faults', in a history entry of its own (Back closes it). */
  openFaultInfo(): void {
    if (!this.link.isOpen(FAULTS_VIEW)) pushSheetEntry(FAULTS_VIEW, this.link, this.zone);
    this.faultInfoOpen = true;
    this.link.openView(FAULTS_VIEW);
  }

  closeFaultInfo(): void {
    this.faultInfoOpen = false;
    this.link.closeView(FAULTS_VIEW);
    leaveSheetEntry(FAULTS_VIEW);
  }

  /** A header click: the grid's sort into the link ("date:desc" is the default, "none" = unsorted).
   *  `key`: 'statistik.sort' (energy grids) or 'statistik.pelletSort'. */
  private onSortChanged(e: SortChangedEvent, key = 'statistik.sort'): void {
    if (e.source !== 'uiColumnSorted') return;
    const items = e.api.getColumnState()
      .filter(c => c.sort)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map(c => `${c.colId}:${c.sort}`);
    const value = items.length ? items : ['none'];
    this.link.setList(key, value.join(',') === DEFAULT_SORT ? [] : value);
  }

  /** New column defs (a language switch, crossing 768 px) re-apply the date column's default sort:
   *  restore the linked one. From 768 px also fit the columns to their content again (the new defs bring
   *  their fixed widths back; without rows yet, onFirstDataRendered does it). */
  private onNewColumnsLoaded(e: NewColumnsLoadedEvent, key = 'statistik.sort'): void {
    if (e.source !== 'gridOptionsChanged') return;
    this.applySort(e.api, key);
    if (!this.compact && e.api.getDisplayedRowCount()) e.api.autoSizeAllColumns();
  }

  /** Apply the link's sort (unknown columns are ignored; nothing valid = the default). */
  private applySort(api: GridApi | null, key = 'statistik.sort'): void {
    if (!api || api.isDestroyed()) return;
    const cols = new Set(api.getColumnState().map(c => c.colId));
    const items = this.link.getList(key);
    let state: ColumnState[] = items[0] === 'none' ? [] : items
      .map(it => it.split(':'))
      .filter(([colId, dir]) => cols.has(colId) && (dir === 'asc' || dir === 'desc'))
      .map(([colId, dir], i) => ({ colId, sort: dir as 'asc' | 'desc', sortIndex: i }));
    if (!state.length && items[0] !== 'none') state = [{ colId: 'date', sort: 'desc', sortIndex: 0 }];
    api.applyColumnState({ state, defaultState: { sort: null, sortIndex: null } });
  }

  buildColDefs(): void {
    const t        = (key: string) => this.translate.instant(key);
    const faultTip = (p: any): string | null => p.data?.faultReason
      ? this.translate.instant('FAULT_REASON.' + p.data.faultReason)
      : null;

    const autarkieRules = {
      'text-success fw-bold': (p: any) => p.value >= 80,
      'text-warning fw-bold': (p: any) => p.value >= 50 && p.value < 80,
      'text-danger fw-bold':  (p: any) => p.value < 50,
    };

    // Compact (< 768 px): flex columns fill the width, the symbol as header (legend line above the grid),
    // kWh without the unit.
    const compact = this.compact;
    const fit = (flex: number, minWidth: number): ColDef => compact ? { flex, minWidth } : {};
    // compact: numeric headers over their right-aligned numbers
    const fitNum = (minWidth: number): ColDef => compact ? { ...fit(1, minWidth), headerClass: 'ag-right-aligned-header' } : {};

    // Column factories — the daily and monthly grids share the kWh + autarkie
    // columns; only the daily grid attaches the fault tooltip to every cell.
    // Compact monthly: whole kWh (a month's yield has four digits, "1088.70" would not fit a phone
    // column), one decimal below 10 kWh so small feed-in / import months do not read as 0.
    const kwhMonth = (v: number): string => Math.abs(v || 0) < 9.95 ? (v || 0).toFixed(1) : (v || 0).toFixed(0);
    const kwhCol = (field: string, headerKey: string, symbol: string, width: number, cls: string, daily: boolean): ColDef => ({
      field, headerName: compact ? symbol : t(headerKey), width, sortable: true,
      ...fitNum(43),
      valueFormatter: p => !compact ? formatKwh(p.value) : daily ? (p.value || 0).toFixed(2) : kwhMonth(p.value),
      cellClass: cls,
      ...(daily ? { tooltipValueGetter: faultTip } : {}),
    });
    const autarkieCol = (tip: boolean): ColDef => ({
      field: 'autarkiePercent', headerName: compact ? '%' : t('COLS.AUTARKIE'), width: 100, sortable: true,
      ...fitNum(46),
      valueFormatter: p => p.value != null ? Math.floor(p.value || 0) + ' %' : '–',
      cellClassRules: autarkieRules,
      ...(tip ? { tooltipValueGetter: faultTip } : {}),
    });
    const utcCol = (field: string, headerKey: string, cls: string): ColDef => ({
      field, headerName: t(headerKey), width: 110, sortable: true,
      valueFormatter: p => p.value ? p.value + ' UTC' : '–',
      cellClass: cls,
      tooltipValueGetter: faultTip,
    });

    this.statsColDefs = [
      {
        field: 'date', headerName: t('COLS.TAG'), width: 120, sortable: true, sort: 'desc',
        ...fit(1.6, 78),
        valueFormatter: p => fmtDate(p.value, this.translate.currentLang),
        tooltipValueGetter: faultTip,
      },
      kwhCol('generationKwh',  'COLS.ERTRAG',      '☀️', 110, 'text-end text-warning', true),
      kwhCol('consumptionKwh', 'COLS.VERBRAUCH',   '🏠', 120, 'text-end text-primary', true),
      kwhCol('gridImportKwh',  'COLS.BEZUG',       '🔌', 100, 'text-end text-danger',  true),
      autarkieCol(true),
      kwhCol('gridExportKwh',  'COLS.EINSPEISUNG', '↑',  120, 'text-end text-success', true),
      // compact: sunrise / sunset and the fault note are shown in the day's hourly overlay instead
      ...(compact ? [] : [
        utcCol('sunriseUtc', 'COLS.SONNENAUFGANG',   'text-end text-warning'),
        utcCol('sunsetUtc',  'COLS.SONNENUNTERGANG', 'text-end text-danger'),
        {
          field: 'faultComment', headerName: t('COLS.HINWEIS'), flex: 1, minWidth: 160,
          valueFormatter: p => p.value ?? '–',
          cellClass: (p: any) => p.data?.faultReason ? 'text-warning fst-italic' : 'text-muted',
          tooltipValueGetter: (p: any) => p.data?.faultComment ?? null,
        } as ColDef,
      ]),
    ];

    this.monthlyStatsColDefs = [
      {
        field: 'date', headerName: t('COLS.MONAT'), width: 130, sortable: true, sort: 'desc',
        ...fit(1.6, 78),
        comparator: (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0,
        valueFormatter: p => compact ? this.fmtMonthYearShort(p.value) : fmtMonthYear(p.value, this.translate.currentLang),
      },
      kwhCol('generationKwh',  'COLS.ERTRAG',      '☀️', 110, 'text-end text-warning', false),
      kwhCol('consumptionKwh', 'COLS.VERBRAUCH',   '🏠', 120, 'text-end text-primary', false),
      kwhCol('gridImportKwh',  'COLS.BEZUG',       '🔌', 100, 'text-end text-danger',  false),
      autarkieCol(false),
      kwhCol('gridExportKwh',  'COLS.EINSPEISUNG', '↑',  120, 'text-end text-success', false),
    ];
  }

  /** "Okt. 2026" from a "YYYY-MM" key (compact monthly grids). */
  private fmtMonthYearShort(ym: string): string {
    if (!ym) return ym;
    return new Date(ym + '-01T00:00:00').toLocaleDateString(uiLocale(), { month: 'short', year: 'numeric' });
  }

  /** "Oktober 2026" (the month select of the compact view). */
  fmtMonthYear(ym: string): string {
    return fmtMonthYear(ym, this.translate.currentLang);
  }

  statsGridHeight(rows: number): number {
    return Math.min(rows * 32 + 38, 600);
  }

  /** Year-filtered; includes faulty days (shown strikethrough in table). */
  private get statsBase(): DayStat[] {
    return this.globalYearFilter === 'all'
      ? this.stats
      : this.stats.filter(s => s.date.startsWith(this.globalYearFilter));
  }

  /** Year-filtered; faulty days excluded unless includeBrokenData is set. */
  private get statsBaseClean(): DayStat[] {
    return this.includeBrokenData ? this.statsBase : this.statsBase.filter(s => !s.faultReason);
  }

  get globalStatYears(): string[] {
    const years = [...new Set(this.stats.map(s => s.date.slice(0, 4)))];
    return years.sort((a, b) => b.localeCompare(a));
  }

  get statsMonths(): string[] {
    const months = [...new Set(this.statsBase.map(s => s.date.slice(0, 7)))];
    return months.sort((a, b) => b.localeCompare(a));
  }

  // ── Memoized row data ────────────────────────────────────────────────────
  // Getters that create a new array on every change-detection cycle cause
  // AG Grid to call setRowData() on every cycle → scroll jumps to top.
  // We cache the result and only rebuild when the actual inputs change.

  /**
   * Cheap content signal for memoization. Includes both ends' date + values so the
   * cache invalidates when today's row grows through the day (it sits at one end of
   * the array regardless of sort order) — fixes the stale-grid bug where the key
   * only tracked length + first date.
   */
  private get statsSignal(): string {
    const h = this.stats[0];
    const t = this.stats[this.stats.length - 1];
    const sig = (s?: DayStat) => s ? `${s.date}:${s.generationKwh}:${s.consumptionKwh}:${s.gridImportKwh}` : '';
    return sig(h) + '|' + sig(t);
  }

  private _filteredStatsKey  = '';
  private _filteredStatsData: DayStat[] = [];

  get filteredStats(): DayStat[] {
    const key = this.globalYearFilter + '|' + this.statsMonthFilter + '|' + this.stats.length
              + '|' + this.statsSignal + '|' + this.includeBrokenData;
    if (key !== this._filteredStatsKey) {
      const base = this.statsBaseClean;
      this._filteredStatsData = this.statsMonthFilter === 'all'
        ? base
        : base.filter(s => s.date.startsWith(this.statsMonthFilter));
      this._filteredStatsKey = key;
    }
    return this._filteredStatsData;
  }

  private _monthlyStatsKey  = '';
  private _monthlyStatsData: DayStat[] = [];

  get monthlyStats(): DayStat[] {
    const key = this.globalYearFilter + '|' + this.stats.length
              + '|' + this.statsSignal + '|' + this.includeBrokenData;
    if (key !== this._monthlyStatsKey) {
      const map = new Map<string, { gen: number; cons: number; imp: number; exp: number }>();
      for (const s of this.statsBaseClean) {
        const ym = s.date.slice(0, 7);
        const e  = map.get(ym) ?? { gen: 0, cons: 0, imp: 0, exp: 0 };
        e.gen  += s.generationKwh;
        e.cons += s.consumptionKwh;
        e.imp  += s.gridImportKwh;
        e.exp  += s.gridExportKwh;
        map.set(ym, e);
      }
      this._monthlyStatsData = [...map.entries()]
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([ym, v]) => ({
          date:             ym,
          generationKwh:    v.gen,
          consumptionKwh:   v.cons,
          gridImportKwh:    v.imp,
          gridExportKwh:    v.exp,
          autarkiePercent:  clampAutarkie(v.cons > 0 ? (1 - v.imp / v.cons) * 100 : 0),
        } as DayStat));
      this._monthlyStatsKey = key;
    }
    return this._monthlyStatsData;
  }

  /** Individual faulty day entries for the hover overlay / the fault-days sheet, newest first. */
  get faultDayList(): DayStat[] {
    return this.statsBase.filter(s => !!s.faultReason).sort((a, b) => b.date.localeCompare(a.date));
  }

  /** Faulty days in the current year filter (all days, not just clean). Single pass. */
  get faultDays() {
    const c = { total: 0, wifi: 0, tigo: 0, freeze: 0, noYield: 0, data: 0 };
    for (const s of this.statsBase) {
      switch (s.faultReason) {
        case 'WIFI_OUTAGE':  c.wifi++;    c.total++; break;
        case 'TIGO_OUTAGE':  c.tigo++;    c.total++; break;
        case 'DEYE_FREEZE':  c.freeze++;  c.total++; break;
        case 'NO_YIELD':     c.noYield++; c.total++; break;
        case 'DATA_MISSING': c.data++;    c.total++; break;
        default: break;
      }
    }
    return c;
  }

  get globalStats() {
    const base      = this.statsBaseClean;
    const totalDays = this.statsBase.length;
    if (!totalDays) return null;
    const nz = (n: number) => n || 0;
    const gen  = nz(base.reduce((s, d) => s + d.generationKwh,  0));
    const cons = nz(base.reduce((s, d) => s + d.consumptionKwh, 0));
    const imp  = nz(base.reduce((s, d) => s + d.gridImportKwh,  0));
    const exp  = nz(base.reduce((s, d) => s + d.gridExportKwh,  0));
    const selfConsumed  = Math.max(0, gen - exp);
    const autarkie      = clampAutarkie(cons > 0 ? (1 - imp / cons) * 100 : 0);
    const co2Saved      = nz(gen * this.CO2_FACTOR / 1000);
    const savings       = nz(selfConsumed * this.ELECTRICITY_PRICE);
    const feedinRevenue = nz(exp * this.FEEDIN_TARIFF);
    const totalBenefit  = nz(savings + feedinRevenue);
    const validDays     = base.length;
    const days          = validDays;
    const amortisation  = this.globalYearFilter === 'all' ? totalBenefit / this.SYSTEM_COST_EUR * 100 : null;
    const annualBenefit = days > 0 ? totalBenefit / days * 365 : 0;
    const paybackYears  = this.globalYearFilter === 'all' && annualBenefit > 0 ? this.SYSTEM_COST_EUR / annualBenefit : null;
    const dates = base.map(s => s.date).sort();
    const since = dates[0] ?? null;
    const until = dates[dates.length - 1] ?? null;
    return { gen, cons, imp, exp, selfConsumed, autarkie, co2Saved,
             savings, feedinRevenue, totalBenefit, days, validDays, totalDays, amortisation,
             paybackYears, annualBenefit, since, until };
  }

  setGlobalYear(year: string): void {
    this.globalYearFilter = year;
    this.statsMonthFilter = 'all';
    this.statsRowLimit = this.statsFirstRows();
    this.link.set('statistik.year', year === 'all' ? null : year);
    this.link.set('statistik.month', null);
  }

  /** Clamp to ≥ 0 and strip −0 before toFixed(). */
  p(n: number): number { return Math.max(0, n || 0); }

  fmtMonth(ym: string): string {
    return fmtMonth(ym, this.translate.currentLang);
  }

  // ── Pellet stats ────────────────────────────────────────────────────────

  readonly pelletGridOptions: GridOptions = {
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    getRowId: (p) => p.data.date,
    onFirstDataRendered: (e) => { if (!this.compact) e.api.autoSizeAllColumns(); },
    // sort in the link as statistik.pelletSort (same format and default "date:desc" as the energy grids)
    onGridReady: (e) => { this._pelletApi = e.api; this.applySort(e.api, 'statistik.pelletSort'); },
    onGridPreDestroyed: (e) => { if (this._pelletApi === e.api) this._pelletApi = null; },
    onSortChanged: (e) => this.onSortChanged(e, 'statistik.pelletSort'),
    onNewColumnsLoaded: (e) => this.onNewColumnsLoaded(e, 'statistik.pelletSort'),
  };

  private _pelletMonthlyKey  = '';
  private _pelletMonthlyData: PelletDayStat[] = [];

  get pelletMonthlyStats(): PelletDayStat[] {
    // Memoized like filteredStats/monthlyStats: today's pelletStats row grows
    // through the day, so the key tracks the last row's value to rebuild only
    // when content actually changes (avoids AG-Grid setRowData on every CD cycle).
    const last = this.pelletStats[this.pelletStats.length - 1];
    const key = this.pelletStats.length + '|' + (last?.date ?? '') + '|' + (last?.consumptionKg ?? '');
    if (key !== this._pelletMonthlyKey) {
      // kg and burn hours add up; the rate while burning is the days' rates weighted by their burn
      // hours (like the backend's day rate, not kg / burn hours: kg burnt in a gap has no burn time)
      const map = new Map<string, { kg: number; burnH: number | null; rateKg: number; rateH: number; gap: boolean; inProgress: boolean }>();
      for (const s of this.pelletStats) {
        const ym = s.date.slice(0, 7);
        const e = map.get(ym) ?? { kg: 0, burnH: null, rateKg: 0, rateH: 0, gap: false, inProgress: false };
        e.kg += s.consumptionKg;
        if (s.burnHours != null) e.burnH = (e.burnH ?? 0) + s.burnHours;
        if (s.avgBurnRateKgH != null && s.burnHours) { e.rateKg += s.avgBurnRateKgH * s.burnHours; e.rateH += s.burnHours; }
        e.gap = e.gap || !!s.dataGap;
        e.inProgress = e.inProgress || !!s.inProgress;
        map.set(ym, e);
      }
      this._pelletMonthlyData = [...map.entries()]
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([ym, v]) => ({
          date: ym, consumptionKg: v.kg, levelPct: null, burnHours: v.burnH,
          avgBurnRateKgH: v.rateH >= 0.25 ? v.rateKg / v.rateH : null,
          dataGap: v.gap, inProgress: v.inProgress,
        }));
      this._pelletMonthlyKey = key;
    }
    return this._pelletMonthlyData;
  }

  pelletGridHeight(rows: number): number {
    return Math.min(rows * 32 + 38, 400);
  }

  pelletTotalKg(): number {
    return this.pelletStats.reduce((s, d) => s + d.consumptionKg, 0);
  }

  buildPelletColDefs(): void {
    const t = (key: string) => this.translate.instant(key);
    // compact as in buildColDefs: the units as headers (legend line above the grid)
    const compact = this.compact;
    const fit = (flex: number, minWidth: number): ColDef => compact ? { flex, minWidth } : {};
    const fitNum = (minWidth: number): ColDef => compact ? { ...fit(1, minWidth), headerClass: 'ag-right-aligned-header' } : {};
    const unit = (u: string) => compact ? '' : ' ' + u;
    const consumptionKgCol = (): ColDef => ({
      field: 'consumptionKg', headerName: compact ? 'kg' : t('HEIZUNG_PELLET_VERBRAUCH') + ' (kg)', width: 160, sortable: true,
      ...fitNum(52),
      // ⚠ = data gap that day (kg burnt in the gap is booked on the day the counter was read again).
      // Compact: whole kg (the counter counts 1-kg steps), so a winter day / month with ⚠ … still fits.
      valueFormatter: (p: any) => p.value != null
        ? p.value.toFixed(compact ? 0 : 1) + unit('kg') + (p.data?.dataGap ? ' ⚠' : '') + (p.data?.inProgress ? ' …' : '') : '-',
      tooltipValueGetter: (p: any) => p.data?.dataGap ? t('HEIZUNG_PELLET_LUECKE') : null,
      cellClass: 'text-end fw-bold',
    });
    const burnHoursCol = (): ColDef => ({
      field: 'burnHours', headerName: compact ? 'h' : t('HEIZUNG_PELLET_COL_BRENNDAUER'), width: 130, sortable: true,
      ...fitNum(44),
      valueFormatter: (p: any) => p.value != null ? p.value.toFixed(1) + unit('h') : '-',
      cellClass: 'text-end',
    });
    const rateCol = (): ColDef => ({
      field: 'avgBurnRateKgH', headerName: compact ? 'kg/h' : t('HEIZUNG_PELLET_COL_RATE'), width: 150, sortable: true,
      ...fitNum(48),
      valueFormatter: (p: any) => p.value != null ? p.value.toFixed(1) + unit('kg/h') : '-',
      cellClass: 'text-end',
    });
    // The boiler's own ~44 kg hopper at the day's last reading (refilled from the store every
    // evening) — not the store level, so no store-style colouring.
    const hopperCol = (): ColDef => ({
      field: 'levelPct', headerName: compact ? '%' : t('HEIZUNG_PELLET_BEHAELTER'), width: 140, sortable: true,
      ...fitNum(44),
      headerTooltip: t('HEIZUNG_PELLET_BEHAELTER_HINT'),
      valueFormatter: (p: any) => p.value != null ? p.value + ' %' : '-',
      cellClass: 'text-end text-muted',
    });

    this.pelletColDefs = [
      {
        field: 'date', headerName: t('COLS.TAG'), width: 120, sortable: true, sort: 'desc' as const,
        ...fit(1.6, 78),
        // compact: weekday + day as in the energy grid ("Di., 20.10.")
        valueFormatter: (p: any) => compact ? fmtDate(p.value, this.translate.currentLang) : fmtDayMonthYear(p.value, this.translate.currentLang),
      },
      consumptionKgCol(),
      burnHoursCol(),
      rateCol(),
      hopperCol(),
    ];
    this.pelletMonthlyColDefs = [
      {
        field: 'date', headerName: t('COLS.MONAT'), width: 140, sortable: true, sort: 'desc' as const,
        ...fit(1.6, 78),
        valueFormatter: (p: any) => compact ? this.fmtMonthYearShort(p.value) : fmtMonthYear(p.value, this.translate.currentLang),
      },
      consumptionKgCol(),
      burnHoursCol(),
      rateCol(),
    ];
  }
}
